import { RclCol, RclText, RclTitle, RclSelect, RclOption, RclFormItem, RclRow, RclButton, RclIcon } from "@risepeopleinc/rcl-react"
import { useEffect, useCallback, useMemo, useState } from "react"
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from "../../../../hooks/reduxHooks";
import { selectPrimaryBankAccount, setOrgs, setPrimaryBankAccount, selectCurrentIntegrationData, selectIsFirstIntegration } from "../../../../reducers/orgSlice";
import {
  setGeneralLedgerAccounts,
  selectGeneralLedgerAccountsLoading,
  setGeneralLedgerAccountsLoading,
  selectGeneralLedgerAccountsGroupedByClass,
  setJournalEntries,
} from "../../../../reducers/journalEntrySlice";
import { selectCurrentOrg, setCurrentOrg } from "../../../../reducers/orgSlice";
import { IGeneralLedgerAccount } from "../../../../interfaces/IJournalEntry";
import useJeApi from "../../../../hooks/useJeApi";
import ArrowRight from "../../../../assets/images/arrow-right";
import './IntegrationConnectStep2.scss';
import useErrorHandling from "../../../../hooks/useErrorHandling";
import DisconnectModal from "../DisconnectXeroModal/DisconnectModal";
import { IOrganization } from "../../../../interfaces/IOrganization";
import KBArticleLink from "../KBArticleLink/KBArticleLink";
import Box from "ui-box";
import { renderGLAccount } from "../../../../helpers";

const IntegrationConnectStep2 = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch()
  const currentOrg = useAppSelector(selectCurrentOrg)
  const isFirstIntegration = useAppSelector(selectIsFirstIntegration);
  const currentIntegration = useAppSelector(selectCurrentIntegrationData);
  const generalLedgerAccountsLoading = useAppSelector(selectGeneralLedgerAccountsLoading)
  const filteredAccounts = useAppSelector(selectGeneralLedgerAccountsGroupedByClass({ filterBank: true, filterSystemAccount: true, sorted: true }));
  const primaryBankAccount = useAppSelector(selectPrimaryBankAccount)
  const { get, patch, remove } = useJeApi()
  const { handleXeroError } = useErrorHandling()
  const [isDisconnecting, setIsDisconnecting] = useState<boolean>(false)

  const spreadFilteredAccounts: IGeneralLedgerAccount[] = useMemo(() => {
    const spreadFilterAccounts: IGeneralLedgerAccount[] = []
    for (const key in filteredAccounts) {
      if (filteredAccounts[key].length) spreadFilterAccounts.push(...filteredAccounts[key])
    }
    return spreadFilterAccounts
  }, [filteredAccounts])

  const getGeneralLedgers = useCallback(async () => {
    try {
      dispatch(setGeneralLedgerAccountsLoading(true))
      const fetchAccounts: IGeneralLedgerAccount[] = await get(`/general-ledger-accounts?organization_id=${currentOrg?.id}`)
      dispatch(setGeneralLedgerAccounts(fetchAccounts))
      dispatch(setGeneralLedgerAccountsLoading(false))
    } catch (err) {
      handleXeroError(err)
    }

  }, [currentOrg?.id, get, dispatch, handleXeroError])

  useEffect(() => {
    getGeneralLedgers();
  }, [getGeneralLedgers])

  const saveXeroSetting = useCallback(async () => {
    if (!currentOrg) return;
    await patch(`/organization?organization_id=${currentOrg?.id}`, { default_credit_account_id: primaryBankAccount })
    const data: IOrganization = ({ ...currentOrg, default_credit_account_id: primaryBankAccount })
    dispatch(setCurrentOrg(data))
    navigate("/dashboard")
  }, [navigate, currentOrg, primaryBankAccount, patch, dispatch])

  const disconnectIntegration = useCallback(async () => {
    if (!currentOrg) return;
    const disconnectEndpoint = currentIntegration?.disconnectEndpoint;
    try {
      setIsDisconnecting(true)
      await remove(`${disconnectEndpoint}?organization_id=${currentOrg.id}`)
      dispatch(setCurrentOrg({ ...currentOrg, xero_org_connections: [], qbo_org_connections: [] }))
      const fetchOrgs = await get<IOrganization[]>('/organizations')
      dispatch(setOrgs(fetchOrgs))
      setPrimaryBankAccount(undefined)
      dispatch(setGeneralLedgerAccounts([]))
      dispatch(setJournalEntries([]))
      navigate('/integration-connect')
      setIsDisconnecting(false)
    } catch (error) {
      setIsDisconnecting(false)
      handleXeroError(error)
    }
  }, [currentOrg, currentIntegration, remove, dispatch, get, navigate, handleXeroError])

  useEffect(() => {
    if (!currentIntegration) navigate("/integration-connect")
  }, [currentIntegration, navigate])

  return (
    <div className="connect-bank-container">
      <div className="connect-bank-wrapper">
        <RclTitle>{isFirstIntegration && 'Welcome! '}Let’s get you set up.</RclTitle>
        <RclRow className="xero-wrapper">
          <div className="connected-with-wrapper">
            <RclIcon className="logo" iconName={`${currentIntegration?.value}Logo`} component={currentIntegration?.icon}></RclIcon>
            <RclCol>
              <RclText className="connect-with-xero-text">Connected With</RclText>
              <br />
              <RclText className="tenant-name-text">{currentIntegration?.name}</RclText>
            </RclCol>
          </div>
          <DisconnectModal disconnectIntegration={disconnectIntegration} isDisconnecting={isDisconnecting} />
        </RclRow>
        <Box marginTop={16}>
          <RclTitle level={2} style={{marginBottom: '4px'}}>3. Confirm your payroll clearing account</RclTitle>
          <RclText className="je-settings-tips">
          We'll use the selected account to pre-fill the credit mappings for all your payroll instructions and service charges. You can switch to a different account in the Settings page.
            <KBArticleLink link='https://support.risepeople.com/portal/en/kb/articles/journal-entry-solution-faqs#11_I_have_to_select_my_payroll_clearing_account_but_I_dont_see_my_bank_account_listed_among_the_options_What_should_I_do' />
          </RclText>
          <br />
          <RclText className="je-settings-tips">
            If you can't find the account you are looking for, or do not have a clearing account, please create one in {currentIntegration?.name}. Please keep in mind they will not allow the use of bank account for journal entries.
          </RclText>
        </Box>
        <Box paddingLeft={24}>
        <RclFormItem>
          {!generalLedgerAccountsLoading ?
            <RclSelect
              placeholder="Select your payroll clearing account"
              defaultValue={undefined}
              onSelect={value => dispatch(setPrimaryBankAccount(JSON.parse(value).id))}
            >
              {spreadFilteredAccounts.map((gl: IGeneralLedgerAccount) =>
                <RclOption key={gl.id} value={JSON.stringify(gl)}>
                  {renderGLAccount(gl, currentIntegration!.value)}
                </RclOption>)}
            </RclSelect>
            :
            <div>LOADING</div>
          }
        </RclFormItem>
        </Box>
        <RclButton className="continue-button"
          size="middle"
          disabled={generalLedgerAccountsLoading || !primaryBankAccount}
          type="primary"
          onClick={saveXeroSetting}
        >
          <p>Get started</p>
          <RclIcon className="arrow-right" iconName="arrowRight" component={ArrowRight}></RclIcon>
        </RclButton>
      </div>
    </div>
  );
}

export default IntegrationConnectStep2
